import React, { useState, useEffect, useRef } from "react";
import "@styles/components/ui/NavigationBar.scss";
import { useSelector } from "react-redux";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { clubsMenuItems } from "@data/HedaerData";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import LanguageIcon from "@mui/icons-material/Language";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useLogoutAndClear } from "@utils/auth";
import { HashLink } from "react-router-hash-link";
import { gaEvent } from "@utils/gaEvent";

const NavigationBar = ({ hasAnnouncement = false }) => {
  const logoutAndClear = useLogoutAndClear();
  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );
  const { i18n } = useTranslation();
  const desktopMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [positionY, setPositionY] = useState(0);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [activeMobileSubMenu, setActiveMobileSubMenu] = useState(null);
  const isChinese = i18n.language === "hk";
  const isMobile = useMediaQuery("(max-width:1280px)");
  const navigate = useNavigate();

  // --- GA helpers (最基本：直接 gaEvent) ---
  const gaMenu = (item, record_id) => {
    if (!item?.event_name || !item?.event_action || !item?.event_label) return;
    gaEvent(item.event_name, item.event_action, item.event_label, record_id);
  };

  useEffect(() => {
    const handleScroll = () => setPositionY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopMenuRef.current &&
        !desktopMenuRef.current.contains(event.target)
      ) {
        setActiveSubMenu(null);
      }

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setActiveMobileSubMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const closeDropdown = () => setActiveSubMenu(null);
    window.addEventListener("scroll", closeDropdown);
    return () => window.removeEventListener("scroll", closeDropdown);
  }, []);

  const handleMenuClick = (key, hasSubMenu) => {
    if (hasSubMenu) {
      setActiveSubMenu((prev) => (prev === key ? null : key));
    } else {
      setActiveSubMenu(null);
    }
  };

  const handleMobileMenuClick = (key, hasSubMenu) => {
    if (hasSubMenu) {
      setActiveMobileSubMenu((prev) => (prev === key ? null : key));
    } else {
      setActiveMobileSubMenu(null);
      setMenuOpen(false);
    }
  };

  const toggleLang = () => {
    const newLang = i18n.language === "hk" ? "hk_en" : "hk";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };


  const topOffset = hasAnnouncement ? (isMobile ? "60px" : "48px") : "0px";

  return (
<Box
      className="navigation-bar-container"
      sx={{
        transition: "all 0.3s ease",
        top: topOffset, // ⭐️ 動態根據有無 announcement 決定 top 距離
        backgroundColor: isMobile
          ? positionY < 80
            ? "transparent"
            : "#000"
          : "#000",
      }}
    >
      <Box className="navigation-bar-content">
        {/* Logo (desktop + mobile) */}
        {isMobile && positionY < 80 ? (
          <img
            className="logo"
            src={`${process.env.PUBLIC_URL}/assets/images/global/mobile_logo.png`}
            alt="logo"
            onClick={() => {
              gaEvent("clubs_menu", "menu_bar", "homepage");
              navigate("/");
            }}
          />
        ) : (
          <img
            className="logo"
            src={`${process.env.PUBLIC_URL}/assets/images/global/logo.png`}
            alt="logo"
            onClick={() => {
              gaEvent("clubs_menu", "menu_bar", "homepage");
              navigate("/");
            }}
          />
        )}

        {/* ✅ Desktop Menu */}
        <Box
          className="desktop-menu"
          ref={desktopMenuRef}
          sx={{ gap: isChinese ? "40px" : "10px" }}
        >
          {clubsMenuItems.map((item) => {
            const { key, path, subMenu, url_hk, url_hk_en } = item;

            const finalHref =
              key === "services" ? (isChinese ? url_hk : url_hk_en) : null;

            const isHashLink = path?.startsWith("/#");

            return (
              <Box key={key} className="navigation-link-container">
                {key === "services" ? (
                  <a
                    href={finalHref || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="navigation-link"
                    onClick={() => {
                      gaMenu(item);
                    }}
                  >
                    <Trans i18nKey={`header:${key}`} />
                  </a>
                ) : isHashLink ? (
                  <HashLink
                    smooth
                    to={path}
                    className="navigation-link"
                    onClick={() => {
                      gaMenu(item);
                    }}
                    scroll={(el) => {
                      setTimeout(() => {
                        const y =
                          el.getBoundingClientRect().top +
                          window.pageYOffset -
                          80;
                        window.scrollTo({ top: y, behavior: "smooth" });
                      }, 1000);
                    }}
                  >
                    <Trans i18nKey={`header:${key}`} />
                  </HashLink>
                ) : (
                  <Typography
                    className="navigation-link"
                    onClick={() => {
                      // ✅ click parent menu 都記一次（包括只展開 submenu）
                      gaMenu(item);

                      if (subMenu) {
                        handleMenuClick(key, true);
                      }
                      if (path) {
                        navigate(path);
                      }
                    }}
                  >
                    <Trans i18nKey={`header:${key}`} />
                    {subMenu &&
                      (activeSubMenu === key ? (
                        <ArrowDropUpIcon className="arrow-icon" />
                      ) : (
                        <ArrowDropDownIcon className="arrow-icon" />
                      ))}
                  </Typography>
                )}

                {subMenu && activeSubMenu === key && (
                  <Box className="nav-sub-dropdown-menu">
                    {subMenu.map((subItem) => (
                      <Box
                        key={subItem.key}
                        className="dropdown-link"
                        onClick={() => {
                          gaMenu(subItem);
                          navigate(subItem.path);
                          setActiveSubMenu(null);
                        }}
                      >
                        <Trans i18nKey={subItem.labelKey} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box className="button-container">
          {memberInfoData && !isMobile && (
            <Button
              className="logout-buutton-container"
              onClick={logoutAndClear}
            >
              <Trans i18nKey="header:logout" />
            </Button>
          )}

          {!isMobile && (
            <Box className="lang-switch-container" onClick={toggleLang}>
              <LanguageIcon className="lang-icon" />
              <Trans i18nKey="header:lang_switch" />
            </Box>
          )}

          {memberInfoData ? (
            <Box
              className="logged-navigation-member-button"
              onClick={() => {
                // 如果你 Excel 有 member zone tracking，可以加返一行 gaEvent
                navigate("/member_zone");
                setMenuOpen(false);
              }}
            >
              <Box className="member-info">
                <Box>
                  <Trans i18nKey="header:hi" />, {memberInfoData?.first_name}
                </Box>
                <Box>
                  {memberInfoData?.pont_balance?.toLocaleString?.() ?? 0}{" "}
                  <Trans i18nKey="header:s_dollar" />
                </Box>
              </Box>
              <AccountCircleIcon className="user-icon" />
            </Box>
          ) : (
            <Button
              className="login-navigation-member-button"
              onClick={() => {
                gaEvent("clubs_menu", "menu_bar", "sign_up");
                navigate("/login");
                setMenuOpen(false);
              }}
            >
              <Trans i18nKey="header:member_button" />
            </Button>
          )}

          <Box className="menu-icon-container">
            {!menuOpen ? (
              <img
                className="menu-button"
                src={`${process.env.PUBLIC_URL}/assets/images/nav/menu_button.png`}
                alt="menu"
                onClick={() => setMenuOpen(true)}
              />
            ) : (
              <img
                className="close-button"
                src={`${process.env.PUBLIC_URL}/assets/images/nav/close_button.png`}
                alt="close"
                onClick={() => setMenuOpen(false)}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* 📱 Mobile Menu */}
      <Box className={`mobile-menu ${menuOpen ? "open" : "close"}`}>
        <Box className="mobile-top-container">
          <img
            className="menu-logo"
            src={`${process.env.PUBLIC_URL}/assets/images/global/logo.png`}
            alt="menu"
            onClick={() => {
              gaEvent("clubs_menu", "menu_bar", "homepage");
              navigate("/");
              setMenuOpen(false);
            }}
          />
        </Box>

        <Box className="mobile-menu-content" ref={mobileMenuRef}>
          <Box className="menu-items">
            {clubsMenuItems.map((item) => {
              const { key, path, icon, subMenu, url_hk, url_hk_en } = item;

              const finalHref =
                key === "services" ? (isChinese ? url_hk : url_hk_en) : null;

              const isHashLink = path?.startsWith("/#");

              return (
                <Box key={key} className="mobile-menu-link-container">
                  <Box>
                    <Box className="mobile-link-container">
                      <img
                        className="mobile-link-icon"
                        src={`${process.env.PUBLIC_URL}${icon}`}
                        alt=""
                      />

                      {key === "services" ? (
                        <a
                          href={finalHref || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mobile-link"
                          onClick={() => {
                            gaMenu(item);
                            setMenuOpen(false);
                          }}
                        >
                          <Trans i18nKey={`header:${key}`} />
                        </a>
                      ) : subMenu ? (
                        <Typography
                          className="mobile-link"
                          onClick={() => {
                            // ✅ 展開 submenu 也記一次 parent click
                            gaMenu(item);
                            handleMobileMenuClick(key, true);
                          }}
                        >
                          <Trans i18nKey={`header:${key}`} />
                          {activeMobileSubMenu === key ? (
                            <ArrowDropUpIcon className="mobile-arrow-icon" />
                          ) : (
                            <ArrowDropDownIcon className="mobile-arrow-icon" />
                          )}
                        </Typography>
                      ) : isHashLink ? (
                        <HashLink
                          smooth
                          to={path}
                          className="mobile-link"
                          onClick={() => {
                            gaMenu(item);
                            setMenuOpen(false);
                          }}
                          scroll={(el) => {
                            setTimeout(() => {
                              const y =
                                el.getBoundingClientRect().top +
                                window.pageYOffset -
                                80;
                              window.scrollTo({ top: y, behavior: "smooth" });
                            }, 500);
                          }}
                        >
                          <Trans i18nKey={`header:${key}`} />
                        </HashLink>
                      ) : (
                        <Typography
                          className="mobile-link"
                          onClick={() => {
                            gaMenu(item);
                            if (path) {
                              navigate(path);
                              setMenuOpen(false);
                            }
                          }}
                        >
                          <Trans i18nKey={`header:${key}`} />
                        </Typography>
                      )}
                    </Box>

                    {/* 📱 Submenu */}
                    {subMenu && activeMobileSubMenu === key && (
                      <Box className="mobile-sub-menu">
                        {subMenu.map((subItem) => (
                          <Typography
                            key={subItem.key}
                            className="dropdown-link"
                            onClick={() => {
                              gaMenu(subItem);
                              navigate(subItem.path);
                              setMenuOpen(false);
                              setActiveMobileSubMenu(null);
                            }}
                          >
                            <SubdirectoryArrowRightIcon className="sub-arrow-right-icon" />
                            <Trans i18nKey={subItem.labelKey} />
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box className="menu-buttons">
            {memberInfoData && (
              <Button
                className="logout-buutton-container"
                onClick={logoutAndClear}
              >
                <Trans i18nKey="header:logout" />
              </Button>
            )}
            <Box className="lang-switch-container" onClick={toggleLang}>
              <LanguageIcon className="lang-icon" />
              <Trans i18nKey="header:lang_switch" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NavigationBar;
